import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Check, Plus, Search, Shield, Trash2, User, X } from 'lucide-react';
import { trpc } from '~/api/trpc';
import Content from '~/components/layout/Content';
import { Badge } from '~/components/ui/badge';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Checkbox } from '~/components/ui/checkbox';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '~/components/ui/dialog';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '~/components/ui/select';
import { Separator } from '~/components/ui/separator';
import { Skeleton } from '~/components/ui/skeleton';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '~/components/ui/table';

import { PERMISSION_LABELS } from './permission-config';

const UserPermissions = () => {
    const navigate = useNavigate();
    const utils = trpc.useUtils();

    const [searchQuery, setSearchQuery] = useState('');
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
    const [addRoleDialogOpen, setAddRoleDialogOpen] = useState(false);
    const [addPermissionDialogOpen, setAddPermissionDialogOpen] = useState(false);
    const [selectedRoleId, setSelectedRoleId] = useState<string>('');
    const [selectedPermission, setSelectedPermission] = useState<string>('');

    // Queries
    const { data: usersData, isLoading: usersLoading } = trpc.permissions.getUsersWithPermissions.useQuery({
        search: searchQuery || undefined,
        page: 0,
        pageSize: 50,
    });

    const { data: roles } = trpc.permissions.getRoles.useQuery();
    const { data: allPermissions } = trpc.permissions.getAllPermissions.useQuery();

    const { data: userDetails, isLoading: userDetailsLoading } = trpc.permissions.getUserPermissionDetails.useQuery(
        { userId: selectedUserId! },
        { enabled: !!selectedUserId }
    );

    // Mutations
    const assignRoleMutation = trpc.permissions.assignRoleToUser.useMutation({
        onSuccess: () => {
            toast.success('Rolle zugewiesen');
            utils.permissions.getUserPermissionDetails.invalidate({ userId: selectedUserId! });
            utils.permissions.getUsersWithPermissions.invalidate();
            utils.permissions.getRoles.invalidate();
            setAddRoleDialogOpen(false);
            setSelectedRoleId('');
        },
        onError: (error) => {
            toast.error('Fehler beim Zuweisen der Rolle', { description: error.message });
        },
    });

    const removeRoleMutation = trpc.permissions.removeRoleFromUser.useMutation({
        onSuccess: () => {
            toast.success('Rolle entfernt');
            utils.permissions.getUserPermissionDetails.invalidate({ userId: selectedUserId! });
            utils.permissions.getUsersWithPermissions.invalidate();
            utils.permissions.getRoles.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Entfernen der Rolle', { description: error.message });
        },
    });

    const addPermissionMutation = trpc.permissions.addUserPermission.useMutation({
        onSuccess: () => {
            toast.success('Berechtigung hinzugefügt');
            utils.permissions.getUserPermissionDetails.invalidate({ userId: selectedUserId! });
            utils.permissions.getUsersWithPermissions.invalidate();
            setAddPermissionDialogOpen(false);
            setSelectedPermission('');
        },
        onError: (error) => {
            toast.error('Fehler beim Hinzufügen der Berechtigung', { description: error.message });
        },
    });

    const removePermissionMutation = trpc.permissions.removeUserPermission.useMutation({
        onSuccess: () => {
            toast.success('Berechtigung entfernt');
            utils.permissions.getUserPermissionDetails.invalidate({ userId: selectedUserId! });
            utils.permissions.getUsersWithPermissions.invalidate();
        },
        onError: (error) => {
            toast.error('Fehler beim Entfernen der Berechtigung', { description: error.message });
        },
    });

    const availableRolesToAdd = roles?.filter(
        (role) => !userDetails?.roles.some((ur) => ur.id === role.id)
    );

    const availablePermissionsToAdd = allPermissions?.filter(
        (p) => !userDetails?.directPermissions.includes(p)
    );

    return (
        <Content>
            <div className='container mx-auto py-10'>
                {/* Header */}
                <div className='flex items-center gap-4 mb-6'>
                    <Button variant='ghost' size='icon' onClick={() => navigate('/permissions')}>
                        <ArrowLeft className='h-5 w-5' />
                    </Button>
                    <div>
                        <h1 className='text-3xl font-bold'>Benutzerberechtigungen</h1>
                        <p className='text-muted-foreground'>
                            Weisen Sie Benutzern Rollen und Berechtigungen zu
                        </p>
                    </div>
                </div>

                <div className='grid grid-cols-1 lg:grid-cols-3 gap-6'>
                    {/* Users list */}
                    <div className='lg:col-span-1'>
                        <Card>
                            <CardHeader>
                                <CardTitle>Benutzer</CardTitle>
                                <CardDescription>
                                    Wählen Sie einen Benutzer aus, um dessen Berechtigungen zu bearbeiten
                                </CardDescription>
                                <div className='relative'>
                                    <Search className='absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground' />
                                    <Input
                                        placeholder='Suchen...'
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className='pl-9'
                                    />
                                </div>
                            </CardHeader>
                            <CardContent className='max-h-[60vh] overflow-y-auto'>
                                {usersLoading ? (
                                    <div className='space-y-2'>
                                        {[1, 2, 3, 4, 5].map((i) => (
                                            <Skeleton key={i} className='h-16 w-full' />
                                        ))}
                                    </div>
                                ) : usersData?.users && usersData.users.length > 0 ? (
                                    <div className='space-y-2'>
                                        {usersData.users.map((user) => (
                                            <div
                                                key={user.id}
                                                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                                                    selectedUserId === user.id
                                                        ? 'border-primary bg-primary/5'
                                                        : 'hover:bg-muted/50'
                                                }`}
                                                onClick={() => setSelectedUserId(user.id)}
                                            >
                                                <div className='flex items-center gap-3'>
                                                    <div className='h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center'>
                                                        <User className='h-5 w-5 text-primary' />
                                                    </div>
                                                    <div className='flex-1 min-w-0'>
                                                        <p className='font-medium truncate'>
                                                            {user.firstName} {user.lastName}
                                                        </p>
                                                        <p className='text-sm text-muted-foreground truncate'>
                                                            {user.email}
                                                        </p>
                                                    </div>
                                                    {user.roles.length > 0 && (
                                                        <Badge variant='secondary' className='shrink-0'>
                                                            {user.roles.length} Rolle{user.roles.length !== 1 ? 'n' : ''}
                                                        </Badge>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className='text-center text-muted-foreground py-4'>
                                        Keine Benutzer gefunden
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </div>

                    {/* User details */}
                    <div className='lg:col-span-2'>
                        {selectedUserId ? (
                            userDetailsLoading ? (
                                <Card>
                                    <CardHeader>
                                        <Skeleton className='h-6 w-48' />
                                    </CardHeader>
                                    <CardContent>
                                        <Skeleton className='h-32 w-full' />
                                    </CardContent>
                                </Card>
                            ) : userDetails ? (
                                <div className='space-y-6'>
                                    {/* Roles */}
                                    <Card>
                                        <CardHeader className='flex flex-row items-center justify-between'>
                                            <div>
                                                <CardTitle className='flex items-center gap-2'>
                                                    <Shield className='h-5 w-5' />
                                                    Zugewiesene Rollen
                                                </CardTitle>
                                                <CardDescription>
                                                    Rollen geben dem Benutzer eine Gruppe von Berechtigungen
                                                </CardDescription>
                                            </div>
                                            <Button
                                                size='sm'
                                                onClick={() => setAddRoleDialogOpen(true)}
                                                disabled={!availableRolesToAdd?.length}
                                            >
                                                <Plus className='h-4 w-4 mr-2' />
                                                Rolle hinzufügen
                                            </Button>
                                        </CardHeader>
                                        <CardContent>
                                            {userDetails.roles.length > 0 ? (
                                                <div className='space-y-3'>
                                                    {userDetails.roles.map((role) => (
                                                        <div
                                                            key={role.id}
                                                            className='flex items-start justify-between p-3 border rounded-lg'
                                                        >
                                                            <div>
                                                                <p className='font-medium'>{role.name}</p>
                                                                {role.description && (
                                                                    <p className='text-sm text-muted-foreground'>
                                                                        {role.description}
                                                                    </p>
                                                                )}
                                                                <div className='flex flex-wrap gap-1 mt-2'>
                                                                    {role.permissions.slice(0, 3).map((p) => (
                                                                        <Badge key={p} variant='outline' className='text-xs'>
                                                                            {PERMISSION_LABELS[p] || p}
                                                                        </Badge>
                                                                    ))}
                                                                    {role.permissions.length > 3 && (
                                                                        <Badge variant='outline' className='text-xs'>
                                                                            +{role.permissions.length - 3}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                            </div>
                                                            <Button
                                                                variant='ghost'
                                                                size='icon'
                                                                onClick={() =>
                                                                    removeRoleMutation.mutate({
                                                                        userId: selectedUserId,
                                                                        roleId: role.id,
                                                                    })
                                                                }
                                                            >
                                                                <Trash2 className='h-4 w-4 text-destructive' />
                                                            </Button>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className='text-muted-foreground text-center py-4'>
                                                    Keine Rollen zugewiesen
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Direct Permissions */}
                                    <Card>
                                        <CardHeader className='flex flex-row items-center justify-between'>
                                            <div>
                                                <CardTitle>Direkte Berechtigungen</CardTitle>
                                                <CardDescription>
                                                    Individuelle Berechtigungen außerhalb von Rollen
                                                </CardDescription>
                                            </div>
                                            <Button
                                                size='sm'
                                                variant='outline'
                                                onClick={() => setAddPermissionDialogOpen(true)}
                                                disabled={!availablePermissionsToAdd?.length}
                                            >
                                                <Plus className='h-4 w-4 mr-2' />
                                                Berechtigung hinzufügen
                                            </Button>
                                        </CardHeader>
                                        <CardContent>
                                            {userDetails.directPermissions.length > 0 ? (
                                                <div className='flex flex-wrap gap-2'>
                                                    {userDetails.directPermissions.map((permission) => (
                                                        <Badge
                                                            key={permission}
                                                            variant='secondary'
                                                            className='flex items-center gap-1 pr-1'
                                                        >
                                                            {PERMISSION_LABELS[permission] || permission}
                                                            <Button
                                                                variant='ghost'
                                                                size='icon'
                                                                className='h-4 w-4 p-0 hover:bg-transparent'
                                                                onClick={() =>
                                                                    removePermissionMutation.mutate({
                                                                        userId: selectedUserId,
                                                                        permission: permission as any,
                                                                    })
                                                                }
                                                            >
                                                                <X className='h-3 w-3' />
                                                            </Button>
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className='text-muted-foreground text-center py-4'>
                                                    Keine direkten Berechtigungen
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>

                                    {/* Effective Permissions */}
                                    <Card>
                                        <CardHeader>
                                            <CardTitle className='flex items-center gap-2'>
                                                <Check className='h-5 w-5 text-green-600' />
                                                Effektive Berechtigungen
                                            </CardTitle>
                                            <CardDescription>
                                                Alle Berechtigungen, die dieser Benutzer hat (aus Rollen + direkt)
                                            </CardDescription>
                                        </CardHeader>
                                        <CardContent>
                                            {userDetails.effectivePermissions.length > 0 ? (
                                                <div className='flex flex-wrap gap-2'>
                                                    {userDetails.effectivePermissions.map((permission) => (
                                                        <Badge key={permission} variant='default'>
                                                            {PERMISSION_LABELS[permission] || permission}
                                                        </Badge>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className='text-muted-foreground text-center py-4'>
                                                    Keine Berechtigungen
                                                </p>
                                            )}
                                        </CardContent>
                                    </Card>
                                </div>
                            ) : null
                        ) : (
                            <Card>
                                <CardContent className='py-20 text-center text-muted-foreground'>
                                    <User className='h-12 w-12 mx-auto mb-4 opacity-50' />
                                    <p>Wählen Sie einen Benutzer aus der Liste aus,</p>
                                    <p>um dessen Berechtigungen zu bearbeiten.</p>
                                </CardContent>
                            </Card>
                        )}
                    </div>
                </div>
            </div>

            {/* Add Role Dialog */}
            <Dialog open={addRoleDialogOpen} onOpenChange={setAddRoleDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Rolle zuweisen</DialogTitle>
                        <DialogDescription>
                            Wählen Sie eine Rolle, die dem Benutzer zugewiesen werden soll.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='py-4'>
                        <Label htmlFor='role'>Rolle</Label>
                        <Select value={selectedRoleId} onValueChange={setSelectedRoleId}>
                            <SelectTrigger>
                                <SelectValue placeholder='Rolle auswählen' />
                            </SelectTrigger>
                            <SelectContent>
                                {availableRolesToAdd?.map((role) => (
                                    <SelectItem key={role.id} value={role.id}>
                                        {role.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => setAddRoleDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={() =>
                                assignRoleMutation.mutate({
                                    userId: selectedUserId!,
                                    roleId: selectedRoleId,
                                })
                            }
                            disabled={!selectedRoleId || assignRoleMutation.isPending}
                        >
                            {assignRoleMutation.isPending ? 'Zuweisen...' : 'Zuweisen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Permission Dialog */}
            <Dialog open={addPermissionDialogOpen} onOpenChange={setAddPermissionDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Berechtigung hinzufügen</DialogTitle>
                        <DialogDescription>
                            Wählen Sie eine Berechtigung, die dem Benutzer direkt zugewiesen werden soll.
                        </DialogDescription>
                    </DialogHeader>
                    <div className='py-4'>
                        <Label htmlFor='permission'>Berechtigung</Label>
                        <Select value={selectedPermission} onValueChange={setSelectedPermission}>
                            <SelectTrigger>
                                <SelectValue placeholder='Berechtigung auswählen' />
                            </SelectTrigger>
                            <SelectContent>
                                {availablePermissionsToAdd?.map((permission) => (
                                    <SelectItem key={permission} value={permission}>
                                        {PERMISSION_LABELS[permission] || permission}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <DialogFooter>
                        <Button variant='outline' onClick={() => setAddPermissionDialogOpen(false)}>
                            Abbrechen
                        </Button>
                        <Button
                            onClick={() =>
                                addPermissionMutation.mutate({
                                    userId: selectedUserId!,
                                    permission: selectedPermission as any,
                                })
                            }
                            disabled={!selectedPermission || addPermissionMutation.isPending}
                        >
                            {addPermissionMutation.isPending ? 'Hinzufügen...' : 'Hinzufügen'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </Content>
    );
};

export default UserPermissions;
